import { getAvatarUrl } from '../../lib/avatarUtils';

interface UserAvatarProps {
    src?: string | null;
    name?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | string;
    isExternal?: boolean;
    className?: string;
    ring?: boolean;
}

export function UserAvatar({
    src,
    name = 'Anonim',
    size = 'md',
    isExternal = false,
    className = '',
    ring = false
}: UserAvatarProps) {
    // Map size keys to tailwind classes
    const sizeMap: Record<string, string> = {
        'xs': 'w-6 h-6',
        'sm': 'w-8 h-8',
        'md': 'w-10 h-10',
        'lg': 'w-12 h-12',
        'xl': 'w-24 h-24',
        '2xl': 'w-28 h-28 md:w-36 md:h-36',
    };

    const sizeClass = sizeMap[size] || size;

    // Define proportional rounding for Mavililer members
    // We want the rounding to be less pronounced at smaller sizes to avoid looking circular
    const roundingMap: Record<string, string> = {
        'xs': 'rounded-[2px]',
        'sm': 'rounded-[4px]',
        'md': 'rounded-[6px]',
        'lg': 'rounded-[8px]',
        'xl': 'rounded-[12px]',
        '2xl': 'rounded-[16px] md:rounded-[22px]',
    };

    const rounding = isExternal ? 'rounded-full' : (roundingMap[size] || 'rounded-md');

    // Internal image rounding should be slightly less to create a "nested border" feel
    const imgRoundingMap: Record<string, string> = {
        'xs': 'rounded-[1px]',
        'sm': 'rounded-[3px]',
        'md': 'rounded-[5px]',
        'lg': 'rounded-[7px]',
        'xl': 'rounded-[10px]',
        '2xl': 'rounded-[14px] md:rounded-[20px]',
    };
    const imgRounding = isExternal ? 'rounded-full' : (imgRoundingMap[size] || 'rounded-sm');

    const avatarUrl = src || getAvatarUrl(name);

    return (
        <div
            className={`
                relative flex-shrink-0 bg-white dark:bg-surface-800 
                flex items-center justify-center p-[2px]
                shadow-sm
                ${sizeClass}
                ${rounding}
                ${!isExternal ? 'border border-gray-100 dark:border-surface-700' : 'border border-surface-200 dark:border-surface-700'}
                ${ring ? 'ring-2 ring-mavi-500/20' : ''}
                ${className}
            `}
        >
            <div className={`
                relative w-full h-full overflow-hidden
                ${imgRounding}
                bg-surface-200 dark:bg-surface-700
            `}>
                <img
                    src={avatarUrl}
                    alt={name}
                    onError={(e) => {
                        e.currentTarget.src = getAvatarUrl(name);
                    }}
                    className="w-full h-full object-cover"
                />
            </div>
        </div>
    );
}
